import {useCallback, useEffect, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {useSelector} from '../../state/hooks';
import {useSyncedPollingSubscriber} from '../../state/polling/hooks';
import {usePart} from '../../state/views/hooks';
import {Ref as PanelRef} from '../../state/views/panel/types';
import type {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {panelIsActive} from '../../util/panelbank';
import * as RunHelpers from '../../util/runhelpers';
import {useKeyInfoQueryContext} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {getTrimmedTitleFromAnyConfig} from '../PanelTitle/getTitleFromConfig';
import {getSharePanelLink} from './FullScreenPanel/fullScreenUtils';

export const useNextAndPreviousPanel = ({
  panelBankSectionConfigRef,
  panelRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigRef;
  panelRef: PanelRef;
}) => {
  const {tick} = useSyncedPollingSubscriber('BucketedQueryManager');
  const history = useHistory();
  const sectionPanelRefs = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].panelRefs
  );
  const currIndex = sectionPanelRefs.findIndex(ref => ref.id === panelRef.id);

  const panels = useSelector(state =>
    sectionPanelRefs.map(ref => state.views.parts[ref.type][ref.id])
  );

  const sectionName = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].name
  );

  const section = usePart(panelBankSectionConfigRef);

  const {isSingleRun} = useSingleRunModeContext();
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();
  const keyTypes = RunHelpers.useKeyTypes(historyKeyInfo.keys);

  const goToPanel = useCallback(
    (index: number, direction: 'next' | 'previous') => {
      let targetIndex = index;
      while (targetIndex >= 0 && targetIndex < panels.length) {
        const targetPanel = panels[targetIndex];
        const isPanelActive = panelIsActive({
          section: {type: section.type} as any,
          panel: targetPanel,
          isSingleRun,
          historyKeyInfo,
          keyTypes,
        });

        if (isPanelActive) {
          // Force a new query by triggering a tick
          tick();
          // Navigate to the new panel
          history.push(getSharePanelLink(targetPanel, sectionName));
          return;
        }
        targetIndex += direction === 'next' ? 1 : -1;
      }
    },
    [
      panels,
      section,
      isSingleRun,
      historyKeyInfo,
      keyTypes,
      tick,
      history,
      sectionName,
    ]
  );

  const goToPreviousPanel = useCallback(
    () => goToPanel(currIndex - 1, 'previous'),
    [goToPanel, currIndex]
  );

  const goToNextPanel = useCallback(
    () => goToPanel(currIndex + 1, 'next'),
    [goToPanel, currIndex]
  );

  const {hasPreviousPanel, hasNextPanel, previousPanelTitle, nextPanelTitle} =
    useMemo(() => {
      const findActivePanel = (
        startIndex: number,
        increment: number,
        endCondition: (i: number) => boolean
      ) => {
        for (let i = startIndex; endCondition(i); i += increment) {
          const panel = panels[i];
          const isPanelActive = panelIsActive({
            section: {type: section.type} as any,
            panel,
            isSingleRun,
            historyKeyInfo,
            keyTypes,
          });
          if (isPanelActive) {
            return {
              hasPanel: true,
              title:
                getTrimmedTitleFromAnyConfig(panel.viewType, panel) ||
                (increment > 0 ? 'Next Panel' : 'Previous Panel'),
            };
          }
        }
        return {
          hasPanel: false,
          title: increment > 0 ? 'Next Panel' : 'Previous Panel',
        };
      };

      const previous = findActivePanel(currIndex - 1, -1, i => i >= 0);
      const next = findActivePanel(currIndex + 1, 1, i => i < panels.length);

      return {
        hasPreviousPanel: previous.hasPanel,
        hasNextPanel: next.hasPanel,
        previousPanelTitle: previous.title,
        nextPanelTitle: next.title,
      };
    }, [currIndex, panels, section, isSingleRun, historyKeyInfo, keyTypes]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if no input elements are focused
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      if (hasPreviousPanel && e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        goToPreviousPanel();
      } else if (hasNextPanel && e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        goToNextPanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasPreviousPanel, hasNextPanel, goToPreviousPanel, goToNextPanel]);

  return {
    goToPreviousPanel,
    goToNextPanel,
    hasPreviousPanel,
    hasNextPanel,
    previousPanelTitle,
    nextPanelTitle,
  };
};
